import React, { useRef, useState } from "react";

import { Box } from "@chakra-ui/react";
import { ReactComponent as SettingsIcon } from "assets/icons/settings.svg";

import { DropDown } from "ui/dropdown";
import { IDropDownRef } from "ui/dropdown/types";
import { Heading4, Paragraph3 } from "ui/typography";

import { ActivateRollOverModal } from "./ActivateRollOverModal";
import { DeactivateRollOverModal } from "./DeactivateRollOverModal";
import { EditConfigurationModal } from "./EditConfigurationModal";
import { PauseResumeEmployeeWithdrawal } from "./PauseResumeEmployeeWithdrawal";
import {
  IODPSettingsProps,
  IODPSettinsOption,
  TPauseResumeEmployeeStatus,
} from "./types";

const DROP_DOWN_WIDTH = 242;

export const ODPSettings = (props: IODPSettingsProps) => {
  const {
    isLoadingOdpSettings,
    handleGetBusinessOdpConfig,
    odpConfig,
    setOdpConfig,
  } = props;
  const dropDownRef = useRef<IDropDownRef | null>(null);
  const [isEditConfigModalOpen, setIsEditConfigModalOpen] = useState(false);
  const [isActivateRollOverModalOpen, setIsActivateRollOverModalOpen] =
    useState(false);
  const [isDeactivateRollOverModalOpen, setIsDeactivateRollOverModalOpen] =
    useState(false);
  const [isPauseResumeModalOpen, setIsPauseResumeModalOpen] = useState(false);
  const [pauseResumeStatus, setPauseResumeStatus] =
    useState<TPauseResumeEmployeeStatus>("pause");

  const handleToggleEditConfigModal = () =>
    setIsEditConfigModalOpen(!isEditConfigModalOpen);
  const handleToggleActivateRollOverModal = () =>
    setIsActivateRollOverModalOpen(!isActivateRollOverModalOpen);
  const handleToggleDeactivateRollOverModal = () =>
    setIsDeactivateRollOverModalOpen(!isDeactivateRollOverModalOpen);
  const handleTogglePauseOrResumeModal = () =>
    setIsPauseResumeModalOpen(!isPauseResumeModalOpen);

  const handleToggleDropdown = () => {
    if (!dropDownRef.current) return;
    dropDownRef.current.handleToggleDropdown();
  };

  const odpSettingsOptions: IODPSettinsOption[] = [
    {
      title: "Edit Configuration",
      handleOnClick: () => {
        handleToggleDropdown();
        handleToggleEditConfigModal();
      },
    },
    {
      title: odpConfig?.rollOver
        ? "Deactivate Roll Over"
        : "Activate Roll Over",
      handleOnClick: () => {
        handleToggleDropdown();
        if (odpConfig.rollOver) {
          handleToggleDeactivateRollOverModal();
        } else {
          handleToggleActivateRollOverModal();
        }
      },
    },
    {
      title: "Pause Employee Withdrawals",
      handleOnClick: () => {
        setPauseResumeStatus("pause");
        handleToggleDropdown();
        handleTogglePauseOrResumeModal();
      },
    },
    {
      title: "Resume Employee Withdrawals",
      handleOnClick: () => {
        setPauseResumeStatus("resume");
        handleToggleDropdown();
        handleTogglePauseOrResumeModal();
      },
    },
  ];

  return (
    <Box className="flex items-center justify-between">
      <Heading4
        lineHeight="24px"
        fontWeight="700"
        textColor="#00171A"
      >
        On-Demand Pay Configuration
      </Heading4>
      <DropDown
        customDropDownButtonCss={{
          gap: "8px",
          padding: "10px 18px",
          border: "1px solid #D0D5DD",
          borderRadius: "8px",
          boxShadow: "0px 1px 2px 0px #1018280D",
          width: "141px",
          height: "40px",
          display: "flex",
          alignItems: "center",
          opacity: isLoadingOdpSettings ? "0.3" : "1",
          cursor: isLoadingOdpSettings ? "not-allowed" : "pointer",
        }}
        dropDownPopUpMenuContentCss={{
          height: "fit-content",
          backgroundColor: "#ffffff",
          border: "1px solid #F2F4F7",
          borderRadius: "8px",
          boxShadow: "0px 12px 16px -4px #10182814",
          padding: 0,
        }}
        dropDownBtnContent={
          <>
            <SettingsIcon />
            <p className="text-sm font-semibold text-[#101828]">Settings</p>
          </>
        }
        dropDownWidth={DROP_DOWN_WIDTH}
        ref={dropDownRef}
        isDisabled={isLoadingOdpSettings}
        hasChevronDownIcon
        allowOutSideClick
      >
        <>
          {odpSettingsOptions.map(({ title, handleOnClick }, idx) => {
            return (
              <Box
                className="h-10 py-[10px] px-4 flex items-center cursor-pointer w-[242px]"
                key={idx}
                onClick={handleOnClick}
              >
                <Paragraph3
                  textColor="#1A202C"
                  lineHeight="20px"
                  fontWeight="600"
                >
                  {title}
                </Paragraph3>
              </Box>
            );
          })}
        </>
      </DropDown>
      <EditConfigurationModal
        isOpen={isEditConfigModalOpen}
        setIsOpen={setIsEditConfigModalOpen}
        handleToggleEditConfigModal={handleToggleEditConfigModal}
        handleGetBusinessOdpConfig={handleGetBusinessOdpConfig}
        odpConfig={odpConfig}
      />
      <ActivateRollOverModal
        isOpen={isActivateRollOverModalOpen}
        setIsOpen={setIsActivateRollOverModalOpen}
        handleToggleModal={handleToggleActivateRollOverModal}
        setOdpConfig={setOdpConfig}
      />
      <DeactivateRollOverModal
        isOpen={isDeactivateRollOverModalOpen}
        setIsOpen={setIsDeactivateRollOverModalOpen}
        handleToggleModal={handleToggleDeactivateRollOverModal}
        setOdpConfig={setOdpConfig}
      />
      <PauseResumeEmployeeWithdrawal
        isOpen={isPauseResumeModalOpen}
        setIsOpen={setIsPauseResumeModalOpen}
        handleToggleModal={handleTogglePauseOrResumeModal}
        status={pauseResumeStatus}
      />
    </Box>
  );
};
